import useSettings from '../hooks/useSettings';
import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  makeStyles,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Badge,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import CodeRoundedIcon from "@material-ui/icons/CodeRounded";
import PeopleIcon from "@material-ui/icons/People";
import ListIcon from "@material-ui/icons/ListAlt";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import LocalAtmIcon from "@material-ui/icons/LocalAtm";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import SettingsBrightnessIcon from "@material-ui/icons/SettingsBrightness";
import { AllInclusive, AttachFile, DeviceHubOutlined } from "@material-ui/icons";
import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import usePlans from "../hooks/usePlans";
import useVersion from "../hooks/useVersion";
import ColorModeContext from "./themeContext";

const useStyles = makeStyles((theme) => ({
  panel: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: theme.palette.background.paper,
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: "14px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: 18,
    color: theme.palette.text.primary,
  },
  backButton: {
    color: theme.palette.text.primary,
    marginRight: 12,
  },
  content: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStylesSoft,
    padding: "4px 0",
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    padding: "14px 20px 6px",
    letterSpacing: "0.5px",
  },
  settingsItem: {
    borderRadius: 8,
    margin: "1px 8px",
    "&:hover": {
      backgroundColor: theme.palette.sidebarHover,
    },
  },
  settingsItemIcon: {
    minWidth: 40,
    color: theme.palette.text.secondary,
  },
  settingsItemText: {
    "& .MuiListItemText-primary": {
      fontSize: 14,
      color: theme.palette.text.primary,
    },
  },
  footer: {
    padding: "8px 0",
    textAlign: "center",
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  versionText: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  themeSelector: {
    display: "flex",
    gap: 8,
    padding: "8px 20px 12px",
  },
  themeOption: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    padding: "10px 6px",
    borderRadius: 10,
    border: `2px solid transparent`,
    cursor: "pointer",
    transition: "all 0.15s ease",
    backgroundColor: theme.palette.background.default,
    "&:hover": {
      backgroundColor: theme.palette.sidebarHover,
    },
  },
  themeOptionActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.sidebarActive,
  },
  themeOptionLabel: {
    fontSize: 11,
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  themeOptionIcon: {
    fontSize: 22,
    color: theme.palette.text.secondary,
  },
}));

function SettingsItem({ icon, label, to, onClick }) {
  const classes = useStyles();

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  return (
    <ListItem
      button
      dense
      component={to ? renderLink : undefined}
      onClick={onClick}
      className={classes.settingsItem}
    >
      {icon && (
        <ListItemIcon className={classes.settingsItemIcon}>{icon}</ListItemIcon>
      )}
      <ListItemText primary={label} className={classes.settingsItemText} />
    </ListItem>
  );
}

const SettingsPanel = ({ onClose, visible }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { whatsApps } = useContext(WhatsAppsContext);
  const { colorMode } = useContext(ColorModeContext);
  const [connectionWarning, setConnectionWarning] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const { getAll: getAllSettings } = useSettings();
  const [aiAgentEnabled, setAiAgentEnabled] = useState("disabled");
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);
  const { getPlanCompany } = usePlans();
  const { getVersion } = useVersion();
  const [version, setVersion] = useState(false);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    async function fetchVersion() {
      const _version = await getVersion();
      if (!cancelled) setVersion(_version.version);
    }
    fetchVersion();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!cancelled) {
        setShowCampaigns(planConfigs.plan.useCampaigns);
        setShowOpenAi(planConfigs.plan.useOpenAi);

        const settings = await getAllSettings(companyId);
        const aiAgentConfig = settings.find((s) => s.key === "aiAgentEnabled");
        if (aiAgentConfig && !cancelled) setAiAgentEnabled(aiAgentConfig.value);
        setShowIntegrations(planConfigs.plan.useIntegrations);
        setShowExternalApi(planConfigs.plan.useExternalApi);
      }
    }
    fetchData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offline = whatsApps.filter((w) =>
          ["qrcode", "PAIRING", "DISCONNECTED", "TIMEOUT", "OPENING"].includes(
            w.status
          )
        );
        setConnectionWarning(offline.length > 0);
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [whatsApps]);

  const handleNavigate = () => {
    onClose();
  };

  return (
    <div className={classes.panel}>
      <div className={classes.header}>
        <IconButton
          onClick={onClose}
          size="small"
          className={classes.backButton}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography className={classes.headerTitle}>Configurações</Typography>
      </div>

      <div className={classes.content}>
        <Typography className={classes.sectionTitle}>Tema</Typography>
        <div className={classes.themeSelector}>
          <div
            className={`${classes.themeOption} ${colorMode.themePreference === "system" ? classes.themeOptionActive : ""}`}
            onClick={() => colorMode.setThemeMode("system")}
          >
            <SettingsBrightnessIcon className={classes.themeOptionIcon} />
            <Typography className={classes.themeOptionLabel}>Sistema</Typography>
          </div>
          <div
            className={`${classes.themeOption} ${colorMode.themePreference === "light" ? classes.themeOptionActive : ""}`}
            onClick={() => colorMode.setThemeMode("light")}
          >
            <Brightness7Icon className={classes.themeOptionIcon} />
            <Typography className={classes.themeOptionLabel}>Claro</Typography>
          </div>
          <div
            className={`${classes.themeOption} ${colorMode.themePreference === "dark" ? classes.themeOptionActive : ""}`}
            onClick={() => colorMode.setThemeMode("dark")}
          >
            <Brightness4Icon className={classes.themeOptionIcon} />
            <Typography className={classes.themeOptionLabel}>Escuro</Typography>
          </div>
        </div>

        <Typography className={classes.sectionTitle}>
          {i18n.t("Gerência")}
        </Typography>
        <SettingsItem
          to="/"
          icon={<DashboardOutlinedIcon />}
          label="Dashboard"
          onClick={handleNavigate}
        />

        {showCampaigns && (
          <>
            <Typography className={classes.sectionTitle}>
              {i18n.t("Campanhas")}
            </Typography>
            <SettingsItem
              to="/campaigns"
              icon={<ListIcon />}
              label={i18n.t("Listagem")}
              onClick={handleNavigate}
            />
            <SettingsItem
              to="/contact-lists"
              icon={<PeopleIcon />}
              label={i18n.t("Listas de Contatos")}
              onClick={handleNavigate}
            />
            <SettingsItem
              to="/campaigns-config"
              icon={<ListIcon />}
              label={i18n.t("Configurações")}
              onClick={handleNavigate}
            />
          </>
        )}

        <Typography className={classes.sectionTitle}>
          {i18n.t("Administração")}
        </Typography>

        {user.super && (
          <SettingsItem
            to="/announcements"
            icon={<AnnouncementIcon />}
            label={i18n.t("mainDrawer.listItems.annoucements")}
            onClick={handleNavigate}
          />
        )}
        {showOpenAi && aiAgentEnabled === "enabled" && (
          <SettingsItem
            to="/prompts"
            icon={<AllInclusive />}
            label={i18n.t("mainDrawer.listItems.prompts")}
            onClick={handleNavigate}
          />
        )}
        {showIntegrations && (
          <SettingsItem
            to="/queue-integration"
            icon={<DeviceHubOutlined />}
            label={i18n.t("mainDrawer.listItems.queueIntegration")}
            onClick={handleNavigate}
          />
        )}
        <SettingsItem
          to="/connections"
          icon={
            <Badge
              badgeContent={connectionWarning ? "!" : 0}
              color="error"
            >
              <SyncAltIcon />
            </Badge>
          }
          label={i18n.t("mainDrawer.listItems.connections")}
          onClick={handleNavigate}
        />
        <SettingsItem
          to="/files"
          icon={<AttachFile />}
          label={i18n.t("mainDrawer.listItems.files")}
          onClick={handleNavigate}
        />
        <SettingsItem
          to="/queues"
          icon={<AccountTreeOutlinedIcon />}
          label={i18n.t("mainDrawer.listItems.queues")}
          onClick={handleNavigate}
        />
        <SettingsItem
          to="/users"
          icon={<PeopleAltOutlinedIcon />}
          label={i18n.t("mainDrawer.listItems.users")}
          onClick={handleNavigate}
        />
        {showExternalApi && (
          <SettingsItem
            to="/messages-api"
            icon={<CodeRoundedIcon />}
            label={i18n.t("mainDrawer.listItems.messagesAPI")}
            onClick={handleNavigate}
          />
        )}
        <SettingsItem
          to="/financeiro"
          icon={<LocalAtmIcon />}
          label={i18n.t("mainDrawer.listItems.financeiro")}
          onClick={handleNavigate}
        />
        <SettingsItem
          to="/settings"
          icon={<SettingsOutlinedIcon />}
          label={i18n.t("mainDrawer.listItems.settings")}
          onClick={handleNavigate}
        />
      </div>

      <div className={classes.footer}>
        <Typography className={classes.versionText}>
          V: {`${version}`}
        </Typography>
      </div>
    </div>
  );
};

export default SettingsPanel;
